import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react'
// REMOVED: useNavigate is no longer needed here.
// import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { base_url } from 'env'
import { AuthContextType, UserModel } from 'types'

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserModel | null>(null)
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  )
  const [isLoading, setIsLoading] = useState(true)
  // REMOVED: useNavigate hook call
  // const navigate = useNavigate()

  const fetchUser = useCallback(async (authToken: string) => {
    try {
      const response = await axios.get(`${base_url}/whoami`, {
        // This specific header is still needed for the initial fetch
        headers: { Authorization: `Bearer ${authToken}` },
      })
      setUser(response.data)
    } catch (error) {
      console.error('Failed to fetch user. Token might be invalid.')
      // If fetching the user fails, log them out
      setToken(null)
      localStorage.removeItem('token')
      setUser(null)
      delete axios.defaults.headers.common['Authorization']
    }
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        // Set the default auth header for all subsequent axios requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        await fetchUser(token)
      }
      setIsLoading(false)
    }
    initializeAuth()
  }, [token, fetchUser])

  const login = async (login: string, password: string): Promise<void> => {
    const response = await axios.post(`${base_url}/login`, { login, password })
    const { access_token } = response.data
    setToken(access_token)
    localStorage.setItem('token', access_token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
    await fetchUser(access_token)
    // REMOVED: Navigation is now handled by the component.
    // navigate('/dashboard')
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
    // REMOVED: Navigation is now handled by the AuthLayout component.
    // navigate('/login')
  }

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token && !!user, // More robust check
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
